import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, Filter, FolderKanban, MoreVertical, Clock, Users } from 'lucide-react';
import { mockProjects } from '../../mock/data';

const ProjectsList = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(true);
  const [filter, setFilter] = useState('all');

  const theme = {
    bg: darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50',
    card: darkMode ? 'bg-[#12121a]' : 'bg-white',
    cardHover: darkMode ? 'hover:bg-[#1a1a24]' : 'hover:bg-slate-50',
    text: darkMode ? 'text-white' : 'text-slate-900',
    textMuted: darkMode ? 'text-slate-400' : 'text-slate-500',
    border: darkMode ? 'border-white/5' : 'border-slate-200',
  };

  // Determine project status from data
  const getProjectStatus = (project) => {
    if (project.manualStatus) return project.manualStatus;
    if (project.progress === 100) return 'completed';
    if (project.progress > 0) return 'active';
    return 'planning';
  };

  const filteredProjects = filter === 'all'
    ? mockProjects
    : mockProjects.filter(p => getProjectStatus(p) === filter);

  const getStatusStyle = (status) => {
    const styles = {
      'active': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      'completed': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'on-hold': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      'planning': 'bg-violet-500/10 text-violet-400 border-violet-500/20',
      'cancelled': 'bg-red-500/10 text-red-400 border-red-500/20',
    };
    return styles[status] || styles.active;
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text}`}>
      {/* Ambient glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-violet-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-40 ${theme.card}/80 backdrop-blur-xl border-b ${theme.border}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/mgmt-app')}
              className="p-2 rounded-xl hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-violet-400" />
              <h1 className="font-semibold">Projects</h1>
            </div>
          </div>
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-xl hover:bg-white/5"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>

        {/* Filters */}
        <div className="px-4 pb-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {['all', 'active', 'completed', 'on-hold', 'planning'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  filter === f
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white'
                    : `${theme.card} ${theme.textMuted} border ${theme.border}`
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1).replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 p-4 space-y-3">
        {filteredProjects.map((project) => (
          <div 
            key={project.id} 
            className={`${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}
          >
            {/* Progress indicator bar */}
            <div className="h-1 bg-gradient-to-r from-violet-700 to-violet-900" style={{ width: `${project.progress}%` }} />
            
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold">{project.name}</h3>
                  <p className={`text-sm ${theme.textMuted}`}>{project.company}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${getStatusStyle(getProjectStatus(project))}`}>
                    {getProjectStatus(project)}
                  </span>
                  <button className="p-1 rounded-lg hover:bg-white/5">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-xs mb-1.5">
                  <span className={theme.textMuted}>Progress</span>
                  <span className="font-medium">{project.progress}%</span>
                </div>
                <div className={`h-2 rounded-full ${darkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                  <div 
                    className="h-full rounded-full bg-gradient-to-r from-violet-700 to-violet-900 transition-all"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
              </div>

              {/* Meta */}
              <div className="flex items-center gap-4 text-xs">
                <div className={`flex items-center gap-1.5 ${theme.textMuted}`}>
                  <Clock className="w-3.5 h-3.5" />
                  <span>{project.dueDate}</span>
                </div>
                <div className={`flex items-center gap-1.5 ${theme.textMuted}`}>
                  <Users className="w-3.5 h-3.5" />
                  <span>{project.teamSize || 4} members</span>
                </div>
                {project.isEscalated && (
                  <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 text-xs">
                    Escalated
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
};

export default ProjectsList;
